// cSpell: words mapboxgl saleticket mapbox gmap

import { SaleTicket } from "../global-commons/type";

/** モバイル表示になる画面幅 */
export const MOBILE_WIDTH = 768;

/** ドロワーの幅 */
export const DRAWER_WIDTH = 500;

/** 現在の表示領域の高さ（iOSの可変UIを考慮） */
export function getViewportHeight(): number {
  // iOS Safariはアドレスバーの開閉で見えている高さが変わるため、
  // visualViewportを優先して「実際に見えている高さ」を取得する。
  return window.visualViewport?.height ?? window.innerHeight;
}

/** モバイル表示時のドロワーの初期表示のtopの値 */
export const MOBILE_DRAWER_INITIAL_TOP = window.innerHeight - 200;

/** モバイル表示時のドロワーの初期表示のtopの値（動的計算） */
export function getMobileDrawerInitialTop(): number {
  // 固定値ではなく現時点の表示領域から計算し直すことで、
  // UI高さ変動後もドロワーの基準位置をずらさない。
  return Math.max(getViewportHeight() - 200, 0);
}

/** モバイル表示時のドロワーの最大表示のtopの値 */
export const MOBILE_DRAWER_ENLARGED_TOP = 0;

/** マウス・タッチを放したとき、ドロワーを最大化するか初期地位に戻すかの閾値 */
export const MOBILE_DRAWER_THRESHOLD_TOP =
  (MOBILE_DRAWER_INITIAL_TOP + MOBILE_DRAWER_ENLARGED_TOP) / 2;

/** マウス・タッチを放したときの閾値（動的計算） */
export function getMobileDrawerThresholdTop(): number {
  // 初期位置が動的に変わるため、閾値も毎回同じ基準で再計算する。
  return (getMobileDrawerInitialTop() + MOBILE_DRAWER_ENLARGED_TOP) / 2;
}

/** モバイル表示時の"検索する"ボタンの高さ */
export const MOBILE_SEARCH_BUTTON_HEIGHT = 100;

/** PC表示時の上部navbar高さ */
export const UPPER_NAVBAR_HEIGHT = 63;

/** PC表示時の中部navbar高さ */
export const MIDDLE_NAVBAR_HEIGHT = 60;

/** マップのズーム時のデフォルトパディング */
export const MAP_DEFAULT_PADDING = 100;

/** 地図のデフォルト緯度経度とズームレベル */
export const DEFAULT_LAT = 37.9581109;
export const DEFAULT_LON = 137.4733595;
export const DEFAULT_ZOOM = 4;

/** 検索遅延ミリ秒 */
export const DELAYED_SEARCH_MILLISECONDS = 250;

/** この変数をtrueにすると、マーカーのラベルにスポットIDを表示する */
export const MARKER_DEBUG = false;

/** マーカーサイズ設定 */
export type MarkerSize = {
  spotFrameSize: number; // 背景のサイズ
  imageSize: number; // アイコン画像のサイズ
  shiftImage: number; // アイコン画像が背景画像がら縦にずれるpx数
  labelVerticalOffset: number; // ラベルの縦位置のオフセット
  labelFontSize: number; // ラベルのフォントサイズ
  duplicateLabelFontSize?: number; // 重複ラベルのフォントサイズ
  duplicateLabelTopOffset?: number; // 重複ラベルの縦位置のオフセット
  duplicateLabelRightOffset?: number; // 重複ラベルの横位置のオフセット
};

export const DEFAULT_MARKER_SIZE: MarkerSize = {
  spotFrameSize: 37,
  imageSize: 30,
  shiftImage: 12,
  labelVerticalOffset: -10,
  labelFontSize: 10,
  duplicateLabelFontSize: 15,
  duplicateLabelTopOffset: -38,
  duplicateLabelRightOffset: -29,
};

export const EMPHASIZED_MARKER_SIZE: MarkerSize = {
  spotFrameSize: 54,
  imageSize: 48,
  shiftImage: 14,
  labelVerticalOffset: -5,
  labelFontSize: 14,
  duplicateLabelFontSize: 15,
  duplicateLabelTopOffset: -38,
  duplicateLabelRightOffset: -29,
};

/** 現在の表示モード
 * mode.mode: "spot" -> 全スポットを表示
 * mode.mode: "singleSpot" -> 1スポットを表示
 * mode.mode: "saleTicket" -> 1販売チケットに属するスポットを表示
 * mode.id -> 現在表示するspot/saleTicketのidを格納
 */
export type Mode = {
  mode: "spots" | "singleSpot" | "saleTicket";
  id: number | null;
};

/** デバイスタイプ */
export type DeviceType = "PC" | "MOBILE";

/** 販売チケットカードUIを作成して返す */
export function createTicketCard(
  saleTicket: SaleTicket,
  i18n: any,
): HTMLElement {
  const card = document.createElement("div");
  card.classList.add("ticket-card", "override-border-color", "my-3");

  const ticketCardImage = document.createElement("div");
  ticketCardImage.classList.add("ticket-card-image");

  card.appendChild(ticketCardImage);

  const img = document.createElement("img");

  if (saleTicket.images.desc) {
    img.src = saleTicket.images.url;
    img.alt = saleTicket.images.desc;
  } else {
    img.src = "/images/no-image.png";
    img.alt = "no image";
  }

  ticketCardImage.appendChild(img);

  const ticketCardDetails = document.createElement("div");
  ticketCardDetails.classList.add("ticket-card-details");

  card.appendChild(ticketCardDetails);

  const ticketCardTitle = document.createElement("div");
  ticketCardTitle.classList.add("ticket-card-title");
  ticketCardTitle.innerText = saleTicket.subject;

  ticketCardDetails.appendChild(ticketCardTitle);

  saleTicket.price.forEach((price: number, index: number) => {
    const ticketCardPrice = document.createElement("div");
    ticketCardPrice.classList.add("ticket-card-price", "override-color");

    const priceClass = saleTicket.priceClass[index] ?? "";
    const minimumPrice = saleTicket.minimumPrice[index]; // null あり
    const priceStr = price.toLocaleString();

    if (minimumPrice != null) {
      ticketCardPrice.innerText = `${priceClass} ${minimumPrice.toLocaleString()} ~ ${priceStr}${
        i18n["map.yen"]
      }`;
    } else {
      ticketCardPrice.innerText = `${priceClass} ${priceStr}${i18n["map.yen"]}`;
    }

    ticketCardDetails.appendChild(ticketCardPrice);
  });

  return card;
}
